/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DeviceModel } from "../../model/device";
import { Locale, LocaleKeys } from "../../model/locale";
import type { AlertOverlayAction } from "../alertOverlay";

/**
 * Shared action ids and button builder for every CRITICAL "Connection Error"
 * overlay — sign-in, app startup and post-sign-in startup all render the same
 * Exit/Retry pair so the experience is identical wherever a blocking retry
 * surfaces.
 *
 * Retry `keepsOverlayOpen` so the overlay stays with a loading state while the
 * failed call is re-issued; the {@link ConnectionRetryOverlayBridge} turns the
 * selection into a wake of the running loop. Exit closes the app through the
 * existing platform abstraction and is offered on TV and VegaOS only — a
 * browser tab cannot close the app the same way (UAW-2577).
 */

/** Action id for the manual Retry button. Keeps the overlay open. */
export const CONNECTION_RETRY_ACTION_ID: string = "connection-retry";
/** Action id for the Exit button. Closes the app (TV only). */
export const CONNECTION_EXIT_ACTION_ID: string = "connection-exit";

/**
 * Quiet warm-up delays (ms) tried before the CRITICAL overlay is shown. The
 * call is retried silently at [1s, 3s, 5s]; only once these are exhausted does
 * the blocking overlay appear and the policy ladder take over. Fed to
 * `RetryMechanism` as `initialRetryDelaysMs` and used to gate the overlay via
 * {@link isConnectionWarmupAttempt}.
 */
export const CONNECTION_WARMUP_RETRY_DELAYS_MS: readonly number[] = [
  1000, 3000, 5000
];

/**
 * Whether a zero-based retry attempt still falls within the silent warm-up, so
 * the overlay must stay hidden. `onRetry` fires per failure with the attempt,
 * so the first {@link CONNECTION_WARMUP_RETRY_DELAYS_MS}`.length` are quiet.
 */
export function isConnectionWarmupAttempt(attempt: number): boolean {
  return attempt < CONNECTION_WARMUP_RETRY_DELAYS_MS.length;
}

/**
 * The standard Exit (TV only) + Retry actions for a connection-error overlay.
 */
export function buildConnectionRetryActions(): AlertOverlayAction[] {
  const actions: AlertOverlayAction[] = [];
  // VegaOS is checked by its injected platform flag rather than left to the
  // user-agent device-type sniff: the app runs there inside a
  // ReactNativeWebView whose UA need not carry a TV marker, so a Vega device
  // could otherwise miss the Exit button it is meant to have.
  if (DeviceModel.isTvDevice() || DeviceModel.isVegOsPlatform()) {
    actions.push({
      id: CONNECTION_EXIT_ACTION_ID,
      label: Locale.getString(LocaleKeys.ACTION_EXIT)
    });
  }
  actions.push({
    id: CONNECTION_RETRY_ACTION_ID,
    label: Locale.getString(LocaleKeys.ACTION_RETRY),
    keepsOverlayOpen: true
  });
  return actions;
}
